import React, {useEffect, useState} from 'react';
import { Button, Form, Grid, Icon, Image, Input, Message, Popup, Segment, Transition } from 'semantic-ui-react';
import Erro from '../../layouts/mensagens/Erro';
import { Doughnut } from 'react-chartjs-2';
import * as FormValidator from '../../../utils/formValidator';
import api from '../../../api';

// paleta fixa e distinta (a anterior sorteava cores a cada renderização)
const CORES_GRAFICO = ['#E83C6E', '#7C3AED', '#0EA5E9', '#F59E0B', '#10B981', '#64748B', '#EC4899', '#14B8A6'];

const EditarVotacaoResultado = ({ visible, votacao, condominio, loadingVotacao, history }) => {
  const [errors, setErrors] = useState({});
  const [sucesso, setSucesso] = useState(false);
  const [loading, setLoading] = useState({ documento: false, perguntas: true });
  const [perguntas, setPerguntas] = useState([]);
  const [formState, setFormState] = useState(votacao);
  const [ata, setAta] = useState(null);

  useEffect(
    () => {
      if (votacao && votacao._id && visible) {
        setFormState(votacao);
        loadPerguntas();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [votacao, visible]
  );

  const loadPerguntas = async () => {
    try {
      setPerguntas(await api.pergunta.findForStats(condominio._id, votacao._id));
      setLoading(p => ({ ...p, perguntas: false }));
    } catch (e) {
      setErrors((e.data && e.data.errors) || {});
    }
  };

  const closeMessage = () => setSucesso(false);

  const onChangeFile = e => {
    const file = e.target.files;
    if (file && file[0] && file[0].type === 'application/pdf') {
      if (FormValidator.sizeLimit(file[0], process.env.REACT_APP_LIMIT_UPLOAD)) {
        setAta(file[0]);
        setErrors({});
      } else {
        setErrors({ documentoAta: `Permitido arquivos de até ${process.env.REACT_APP_LIMIT_UPLOAD}mb` });
      }
    }
  };

  const onSubmitDocumento = async () => {
    if (ata) {
      setLoading(p => ({ ...p, documento: true }));

      const formData = new FormData();

      formData.append('field', 'documentoAta');
      formData.append('condominio', condominio._id);
      formData.append('arquivo', ata);

      try {
        const documentoAta = await api.votacao.atualizarDocumento(votacao._id, formData);
        setFormState(p => ({ ...p, documentoAta }));
        setLoading(p => ({ ...p, documento: false }));
        setSucesso(true);
        setAta(null);
      } catch (e) {
        setErrors((e.data && e.data.errors) || {});
      }
    } else {
      setErrors({ documentoAta: 'Selecione um arquivo PDF' });
    }
  };

  const getDocLink = () =>
    !!formState.documentoAta && formState.documentoAta.url ? (
      <a href={formState.documentoAta.url} target="_blank" rel="noopener noreferrer">Ver documento atual</a>
    ) : (
      <>Nenhum documento anexado</>
    );

  const getDocLabel = () =>
    !!formState.documentoAta && formState.documentoAta.url ? 'Alterar documento atual' : 'Selecionar documento PDF';

  const goToChat = (_, { id }) => history.push(`/condominios/${condominio._id}/votacoes/chat/${id}`);

  const getTotalVotos = pergunta =>
    pergunta.respostas.map(it => it.votos.length).reduce((acc, count) => acc + count, 0);

  // percentual exibido: ponderado pela fração ideal quando a pauta usa fração
  const getPercentual = (pergunta, resposta, total) => {
    if (pergunta.fracao) return parseFloat(resposta.porcentagem) || 0;
    return total > 0 ? (resposta.votos.length * 100) / total : 0;
  };

  const fmtPct = valor => `${Number(valor).toFixed(1).replace('.', ',')}%`;

  const renderResultado = pergunta => {
    const total = getTotalVotos(pergunta);
    const maisVotos = Math.max(0, ...pergunta.respostas.map(it => it.votos.length));
    const lideres = pergunta.respostas.filter(it => maisVotos > 0 && it.votos.length === maisVotos);
    const empate = lideres.length > 1;

    return (
      <section className="va-resultado" key={pergunta._id}>
        <header className="va-resultado-topo">
          <div>
            <h3>{pergunta.titulo}</h3>
            <div className="va-resultado-meta">
              <span><Icon name="check square outline" /> {total} voto{total !== 1 && 's'}</span>
              {lideres.length > 0 && (
                <span className={`va-resultado-lider ${empate ? 'empate' : ''}`}>
                  <Icon name={empate ? 'balance scale' : 'trophy'} />
                  {empate ? 'Empate entre ' + lideres.map(it => it.titulo).join(' e ') : `Mais votada: ${lideres[0].titulo}`}
                </span>
              )}
            </div>
          </div>
          <Button className="btn-ui-1" id={pergunta._id} onClick={goToChat}>
            <Icon name="comments outline" /> Chat
          </Button>
        </header>

        {total === 0 ? (
          <div className="va-resultado-vazio"><Icon name="inbox" /> Nenhum voto registrado nesta pauta ainda.</div>
        ) : (
          <div className="va-resultado-corpo">
            <ul className="va-resultado-lista">
              {pergunta.respostas.map((resposta, i) => {
                const pct = getPercentual(pergunta, resposta, total);
                const cor = CORES_GRAFICO[i % CORES_GRAFICO.length];
                const lider = maisVotos > 0 && resposta.votos.length === maisVotos;
                return (
                  <li key={resposta._id} className={lider ? 'lider' : ''}>
                    <div className="va-resultado-linha">
                      <span className="va-resultado-marca" style={{ background: cor }} />
                      <span className="va-resultado-nome">{resposta.titulo}</span>
                      <span className="va-resultado-num">
                        <b>{fmtPct(pct)}</b>
                        <small>{resposta.votos.length} voto{resposta.votos.length !== 1 && 's'}</small>
                      </span>
                    </div>
                    <div className="va-resultado-barra"><i style={{ width: `${Math.min(pct, 100)}%`, background: cor }} /></div>
                  </li>
                );
              })}
            </ul>
            <div className="va-resultado-grafico">
              <Doughnut
                data={{
                  labels: pergunta.respostas.map(it => it.titulo),
                  datasets: [{
                    data: pergunta.respostas.map(it => (pergunta.fracao ? it.porcentagem : it.votos.length)),
                    backgroundColor: pergunta.respostas.map((_, i) => CORES_GRAFICO[i % CORES_GRAFICO.length]),
                    borderColor: '#fff',
                    borderWidth: 3,
                    hoverBorderWidth: 3,
                  }],
                }}
                options={{
                  maintainAspectRatio: true,
                  cutoutPercentage: 64,
                  legend: { display: false },
                  tooltips: {
                    callbacks: {
                      label: (item, data) => {
                        const valor = data.datasets[0].data[item.index];
                        const soma = data.datasets[0].data.reduce((a, b) => a + (parseFloat(b) || 0), 0);
                        const pct = soma > 0 ? (valor * 100) / soma : 0;
                        return ` ${data.labels[item.index]}: ${fmtPct(pct)} (${pergunta.respostas[item.index].votos.length} voto${pergunta.respostas[item.index].votos.length !== 1 ? 's' : ''})`;
                      },
                    },
                  },
                }}
              />
              <div className="va-resultado-centro"><b>{total}</b><small>voto{total !== 1 && 's'}</small></div>
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <Transition
      animation="fade up"
      duration={{ show: 700, hide: 0 }}
      visible={visible}
      transitionOnMount={true}
    >
      <Segment>
        <Segment>
          <Grid doubling>
            <Grid.Column width={10}>
              <h3><strong>Ata Final</strong></h3>
              <Form.Field error={!!errors.documentoAta} inline>
                <div style={{ position: "relative" }}>
                  <Transition animation="fade up" duration={{ show: 1000, hide: 400 }} visible={sucesso}>
                    <Message positive size="tiny" onDismiss={closeMessage}>
                      <Icon name='check' /> Documento alterado com sucesso!
                    </Message>
                  </Transition>
                  <div style={{ display: 'inline-block' }}>
                    <Popup
                      trigger={
                        <label htmlFor="documentoAta" className="label-ui-arquivo">
                          <span>
                            {ata ? (
                              <><Icon name="file pdf outline"/> {ata.name}</>
                            ) : (
                              <><Icon name="paperclip"/> {getDocLabel()}</>
                            )}
                          </span>
                          <Input
                            type="file"
                            id="documentoAta"
                            name="documentoAta"
                            style={{ display: 'none' }}
                            onChange={onChangeFile}
                            accept="application/pdf"
                          />
                        </label>
                      }
                    >
                      <Popup.Header>Selecione um documento PDF</Popup.Header>
                      <Popup.Content>
                          Ao adicionar um documento ele ficará disponível no resultado da votação para todos os usuários.
                      </Popup.Content>
                    </Popup>
                    <br />
                    {getDocLink()}
                  </div>
                  <div className="div-ui-acao">
                    {!!ata && (
                      <Button
                        disabled={loading.documento || loadingVotacao}
                        loading={loading.documento}
                        size="small"
                        className="btn-ui-1"
                        onClick={onSubmitDocumento}
                      >
                        {!!formState.documentoAta && formState.documentoAta.url ? 'Confirmar Alteração' : 'Enviar Ata Final'}
                      </Button>
                    )}
                  </div>
                </div>
                <Erro text={errors.documentoAta} />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={6} verticalAlign='middle'>
              <a
                href="https://uniproof.com.br/landing-votecondominio/"
                target="_blank"
                rel="noreferrer noopener"
                style={{ marginTop: 10 }}
              >
                Clique no logo abaixo para ver como enviar e registrar sua ata eletronicamente em cartório.
                <Image
                  src="/images/logo-uniproof.svg"
                  className="ui button btn-ui-1"
                  centered
                  style={{ marginTop: 10 }}
                />
              </a>
            </Grid.Column>
          </Grid>
        </Segment>
        <Segment loading={loading.perguntas}>
          {perguntas.length > 0 ? (
            <div>
              <div style={{ marginBottom: "0px" }}>
                <h3><strong>Resultados</strong></h3>
              </div>
              {perguntas.map(renderResultado)}
            </div>
          ) : (
            <div style={styles.vazio}>
              <p>Nenhuma pergunta / pauta criada ainda</p>
            </div>
          )}
        </Segment>
      </Segment>
    </Transition>
  );
};

const styles = {
  vazio: {
    padding: "30px 10px 20px 10px",
    textAlign: "center",
    fontSize: "13pt",
    fontWeight: 100,
    opacity: .7
  },
};

export default EditarVotacaoResultado;
